import type { PortfolioCard } from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { PORTFOLIO_PATH } from '@/shared/config';
import { buttonVariants } from '@/shared/ui';
import { PortfolioCarousel } from './portfolio-carousel';

interface HomePortfolioProps {
  readonly items: readonly PortfolioCard[];
  readonly isPending?: boolean;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
}

/**
 * HOME 포트폴리오 — 추천 상품과 같은 머리(작은 영문 머리말·굵은 제목·테두리 알약 링크) 아래
 * 제작 사례를 가로 캐러셀로 보여 준다(데스크톱 세 장씩, 포트폴리오 목록과 같은 사례 카드).
 * 성공한 빈 목록은 섹션을 생략하고, 초기 로딩·실패는 별도로 안내한다.
 */
export const HomePortfolio = ({
  items,
  isPending = false,
  isError = false,
  onRetry,
}: HomePortfolioProps): ReactElement | null => {
  if (items.length === 0 && !isPending && !isError) return null;
  return (
    <section aria-labelledby="home-portfolio-heading" className="w-full px-5 sm:px-8 xl:px-14">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-3">
          <p className="text-base text-muted-foreground sm:text-lg">Case Study</p>
          <h2
            id="home-portfolio-heading"
            className="text-[2rem] leading-tight font-bold tracking-[-0.03em] sm:text-[2.75rem]"
          >
            포트폴리오
          </h2>
        </div>
        <Link
          to={PORTFOLIO_PATH}
          className="inline-flex h-12 items-center gap-2 self-start rounded-full border border-foreground px-6 text-[15px] font-medium transition-colors hover:bg-foreground hover:text-background sm:self-auto"
        >
          포트폴리오 보기
          <Plus aria-hidden strokeWidth={1.75} className="size-4" />
        </Link>
      </div>

      {isPending && items.length === 0 ? (
        <p role="status" className="mt-10 text-muted-foreground">
          포트폴리오를 불러오는 중입니다.
        </p>
      ) : null}
      {isError ? (
        <div
          role="alert"
          className="mt-10 flex flex-wrap items-center justify-between gap-4 border-y border-foreground/10 py-5"
        >
          <p className="text-sm text-destructive">포트폴리오를 불러오지 못했습니다.</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              다시 시도
            </button>
          ) : null}
        </div>
      ) : null}
      {items.length > 0 ? <PortfolioCarousel items={items} onRetry={onRetry} /> : null}
    </section>
  );
};
